import { skipToken } from "@reduxjs/toolkit/query/react";
import { holdEligibilityActivity } from "pages/CreateOrder/hooks/eligibilityActivity";
import { Toast } from "primereact/toast";
import { RefObject, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useEvaluatePracticeTypeSuggestionQuery } from "store/queries/practiceTypeSuggestion";
import { IPracticeTypeSuggestionEvaluateRequest } from "types/PracticeTypeSuggestion/PracticeTypeSuggestion";

import { buildEligibilityProcedures } from "./eligibilityProcedures";
import {
  IPracticeTypeRunCompletedEventDetail,
  IPracticeTypeRunSettledEventDetail,
  PRACTICE_TYPE_RUN_COMPLETED_EVENT,
  PRACTICE_TYPE_RUN_SETTLED_EVENT,
  requestPracticeTypeRun,
} from "./practiceTypeRunEvent";
import { buildPracticeTypeRerunMessage } from "./practiceTypeSwitchMessage";

/** A user typing several CPT codes in a row causes one evaluation, not one per code. */
export const PRACTICE_TYPE_RERUN_DEBOUNCE_MS = 800;

/**
 * CLE-2971 — the one page-side trigger of the Service Type switch.
 *
 * The API switches the Service Type inside the eligibility run, from the CPT codes the request
 * carries. Lincoln staff often run eligibility FIRST and type the CPT codes after, so that run had no
 * codes to switch on; only the page knows a code was added. When the order's set of CPT codes changes
 * after a check ran IN THIS PAGE SESSION, this asks the evaluate endpoint (codes, payer, plan name from
 * the last response, the code last run) and, if a rule applies, re-runs that side's check ONCE as a
 * plain run — the API then does the switch and says so on the response.
 *
 * Guards:
 *  - armed only by a run this page session made (`PRACTICE_TYPE_RUN_COMPLETED_EVENT`), never by mount
 *    or by loading a draft / copy / redo, which restore benefits without running anything;
 *  - compared against the codes that run was SENT with, so codes typed while it was in flight still
 *    count once it completes;
 *  - never after a "Keep …" run: the estimator chose that Service Type;
 *  - at most one automatic re-run per target code per page session;
 *  - debounced, and the answer is read from `currentData`, which belongs to the arguments asked —
 *    a stale answer for other codes can never trigger a run;
 *  - a request arriving while a run is in flight is held by `EligibilityButton` and run when that
 *    run settles (latest wins), never dropped.
 *
 * While it works, the estimate cannot be created: it holds the page's pending-eligibility counter
 * (`eligibilityActivity.ts`) from the moment the codes differ from the last run's (the debounce is
 * armed), through the evaluate call, and — when it asks for a re-run — until that side's run settles
 * (`PRACTICE_TYPE_RUN_COMPLETED_EVENT` or `PRACTICE_TYPE_RUN_SETTLED_EVENT`, which also covers a failed
 * run). An answer with no suggestion, a target already fired, a failed evaluate call, no button to take
 * the request, and unmount all release it.
 *
 * The moment it asks for the re-run it shows a sticky "Re-running eligibility…" toast on the side's
 * own `rerunToastRef` (PrimeReact 8's `remove()` cannot reach one message on the shared app Toast, so
 * clearing this dedicated one is the only way to take exactly this message down), cleared when the
 * hold is released. `EligibilityButton`'s "Service Type changed" toast still announces the result.
 */
export function usePracticeTypeAutoRerun({
  accountId,
  isSecondaryInsurance = false,
  rerunToastRef,
}: {
  accountId: string;
  isSecondaryInsurance?: boolean;
  rerunToastRef?: RefObject<Pick<Toast, "show" | "clear">>;
}) {
  const { watch, getValues, setValue } = useFormContext();
  const serviceDetails = watch("serviceDetails");
  const benefitDetails = watch(
    isSecondaryInsurance ? "benefitDetails2" : "benefitDetails"
  );
  const payer = watch(isSecondaryInsurance ? "payer2" : "payer");

  const procedureCodes = useMemo(
    () =>
      buildEligibilityProcedures(serviceDetails)
        .map((procedure) => procedure.code)
        .sort(),
    [serviceDetails]
  );
  const codesKey = procedureCodes.join(",");

  // Latest values for the debounced callback and the run listener.
  const latest = useRef({ procedureCodes, codesKey, benefitDetails, payer });
  latest.current = { procedureCodes, codesKey, benefitDetails, payer };

  /** The codes as of the last run (or evaluation) in this session; null = no run yet, so disarmed. */
  const baselineRef = useRef<string | null>(null);
  const lastRunWasKeepRef = useRef(false);
  const firedTargetsRef = useRef(new Set<string>());
  const [args, setArgs] =
    useState<IPracticeTypeSuggestionEvaluateRequest | null>(null);
  // Bumped by every completed run, so the codes are compared again even when they did not change since.
  const [runCount, setRunCount] = useState(0);

  // The pending-eligibility hold: wanted while the debounce is armed, the evaluate call is out, or the
  // re-run this asked for has not settled. ONE hold per side, taken and released by `sync`.
  const activity = useRef({
    timerArmed: false,
    evaluating: false,
    awaitingRun: false,
    toastShown: false,
    release: null as (() => void) | null,
  });
  const formRef = useRef({ getValues, setValue });
  formRef.current = { getValues, setValue };
  const toastRef = useRef(rerunToastRef);
  toastRef.current = rerunToastRef;
  const helpers = useRef({
    sync: () => {
      const state = activity.current;
      const wanted = state.timerArmed || state.evaluating || state.awaitingRun;
      if (wanted && !state.release)
        state.release = holdEligibilityActivity(formRef.current);
      if (!wanted && state.release) {
        state.release();
        state.release = null;
      }
    },
    hideToast: () => {
      if (!activity.current.toastShown) return;
      activity.current.toastShown = false;
      toastRef.current?.current?.clear();
    },
    /** The re-run this asked for has settled (or will never happen). */
    runSettled: () => {
      activity.current.awaitingRun = false;
      helpers.current.hideToast();
      helpers.current.sync();
    },
  });

  useEffect(() => {
    const { current } = helpers;
    const forThisSide = (detail?: { isSecondaryInsurance?: boolean }) =>
      !!detail && Boolean(detail.isSecondaryInsurance) === isSecondaryInsurance;
    const onCompleted = (event: Event) => {
      const { detail } =
        event as CustomEvent<IPracticeTypeRunCompletedEventDetail>;
      if (!forThisSide(detail)) return;
      // The codes the run was SENT with: a code typed while it was in flight still counts as a change.
      baselineRef.current = detail.procedureCodes
        ? [...detail.procedureCodes].sort().join(",")
        : latest.current.codesKey;
      lastRunWasKeepRef.current = detail.keepPracticeType === true;
      setArgs(null);
      activity.current.evaluating = false;
      // The run's own button keeps the page pending until the run settles, so this cannot open a gap.
      current.runSettled();
      setRunCount((count) => count + 1);
    };
    const onSettled = (event: Event) => {
      const { detail } =
        event as CustomEvent<IPracticeTypeRunSettledEventDetail>;
      if (!forThisSide(detail) || !activity.current.awaitingRun) return;
      current.runSettled();
    };
    window.addEventListener(PRACTICE_TYPE_RUN_COMPLETED_EVENT, onCompleted);
    window.addEventListener(PRACTICE_TYPE_RUN_SETTLED_EVENT, onSettled);
    return () => {
      window.removeEventListener(
        PRACTICE_TYPE_RUN_COMPLETED_EVENT,
        onCompleted
      );
      window.removeEventListener(PRACTICE_TYPE_RUN_SETTLED_EVENT, onSettled);
    };
  }, [isSecondaryInsurance]);

  // Unmount: never leave the page pending, or the toast up.
  useEffect(() => {
    const { current } = helpers;
    const state = activity.current;
    return () => {
      state.timerArmed = false;
      state.evaluating = false;
      state.awaitingRun = false;
      current.hideToast();
      current.sync();
    };
  }, []);

  useEffect(() => {
    const { current } = helpers;
    const state = activity.current;
    if (
      baselineRef.current === null ||
      lastRunWasKeepRef.current ||
      !accountId ||
      codesKey === "" ||
      codesKey === baselineRef.current
    ) {
      state.timerArmed = false;
      current.sync();
      return undefined;
    }
    state.timerArmed = true;
    current.sync();
    const timer = setTimeout(() => {
      state.timerArmed = false;
      const { benefitDetails: stored, payer: currentPayer } = latest.current;
      const ranUnder: string | undefined =
        stored?.request?.practiceTypeCode || undefined;
      if (!ranUnder) {
        current.sync();
        return;
      }
      baselineRef.current = latest.current.codesKey;
      state.evaluating = true;
      current.sync();
      setArgs({
        accountId,
        payerId: currentPayer?.payerId ?? null,
        planName: stored?.response?.planCoverageSummary?.PlanName ?? null,
        practiceTypeCode: ranUnder,
        procedureCodes: latest.current.procedureCodes,
      });
    }, PRACTICE_TYPE_RERUN_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [codesKey, accountId, runCount]);

  const { currentData, isError } = useEvaluatePracticeTypeSuggestionQuery(
    args ?? skipToken
  );

  useEffect(() => {
    if (!args || (!currentData && !isError)) return;
    const { current } = helpers;
    const state = activity.current;
    setArgs(null);
    state.evaluating = false;
    const suggestion = isError ? null : currentData?.suggestion;
    const target = suggestion?.targetPracticeTypeCode;
    if (!suggestion || !target || firedTargetsRef.current.has(target)) {
      current.sync();
      return;
    }
    firedTargetsRef.current.add(target);
    state.awaitingRun = true;
    current.sync();
    // Before the run starts, so the estimator hears about it now, not when pVerify answers.
    current.hideToast();
    toastRef.current?.current?.show({
      severity: "info",
      summary: "Changing Service Type",
      detail: buildPracticeTypeRerunMessage({
        from: args.practiceTypeCode,
        to: target,
        modality: suggestion.modality,
      }),
      sticky: true,
    });
    state.toastShown = true;
    if (!requestPracticeTypeRun({ isSecondaryInsurance })) current.runSettled();
  }, [args, currentData, isError, isSecondaryInsurance]);
}
